import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as React from "react";

import Card from "./Card";
import { CommentsContext } from "../context";
import { IComment } from "../types";
import Link from "../../Link/Link";
import { useOutsideEvent } from "../../../hooks";

interface IThreadProps {
  comments: IComment[];
  isBlured?: boolean;
  isShowed?: boolean;
  focusedCommentId?: string;
  onResolve: (id: string, state: boolean) => void;
  onShowToggled: (state: boolean) => void;
  useGrid: boolean;
  isGlobal?: boolean;
  className?: string;
}

function List(props: IThreadProps) {
  const listWrapperElement = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const ctx = useContext(CommentsContext);

  const [isHovered, setIsHovered] = useState(false);
  const [isResolvedShowed, setIsResolvedShowed] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState("");
  const [isScrollable, setIsScrollable] = useState(false);

  const { comments = [] } = props;
  const { t } = useTranslation();

  // ---- helpers: корни и сортировка ----
  // Данные держим ASC (oldest → newest), а рендерим .reverse() => визуально newest → oldest
  const sortByCreatedAsc = (a: IComment, b: IComment) => {
    if (a.created && b.created) {
      return new Date(a.created).getTime() - new Date(b.created).getTime();
    }
    return 0;
  };
  const rootsAsc = useCallback((arr: IComment[]) => {
    return [...arr].filter((c) => !c.replyId).sort(sortByCreatedAsc);
  }, []);

  // Статус «все менеджерские решены»
  const rootManagerComments = comments.filter((c) => c.from === "manager");
  const resolvedManagerCommentsCount = rootManagerComments.filter(
    (c) => c.isResolved
  ).length;
  const allManagerCommentsResolved =
    rootManagerComments.length > 0 &&
    resolvedManagerCommentsCount === rootManagerComments.length;

  const toggleShow = useCallback(
    (state: boolean) => {
      if (!state) setIsResolvedShowed(false);
      props.onShowToggled?.(state);
    },
    [props]
  );

  const resolvedCommentsCount = comments.filter((c) => c.isResolved)?.length;
  const allCommentsResolved = resolvedCommentsCount === comments.length;
  const showResolved =
    isResolvedShowed || (allCommentsResolved && ctx.isManager);

  // ЕДИНЫЙ ПОРЯДОК ДЛЯ ВСЕХ (клиент/админ, открыто/закрыто):
  // берём ТОЛЬКО корневые, ASC в данных, рендерим .reverse() => на экране newest → oldest
  const commentsForShow = useMemo(() => {
    let base = rootsAsc(comments);

    // Админ: при включённом "show resolved" оставляем только резолвнутые / активную / новые
    if (ctx.isManager && showResolved) {
      base = base.filter(
        (c) => c.isResolved || c.id === activeCommentId || c.isNew
      );
    }

    return base;
  }, [comments, activeCommentId, showResolved, ctx.isManager, rootsAsc]);

  useOutsideEvent(listWrapperElement, () => {
    const newComment = commentsForShow.find((item) => item.isNew);
    if (newComment) {
      ctx.deleteComment?.(newComment.id);
    }
  });

  // Автоскролл к блоку при открытии
  useEffect(() => {
    const el = listWrapperElement.current;
    if (props.isShowed && el) {
      const timeout = setTimeout(() => {
        const top = el.getBoundingClientRect().top + window.scrollY - 40;
        window.scrollTo({ top, behavior: "smooth" });
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [props.isShowed]);

  // Появление градиента при скролле
  useEffect(() => {
    const el = scrollableRef.current;
    if (!el) return;

    const checkScroll = () => {
      setIsScrollable(el.scrollHeight > el.clientHeight + 4);
    };

    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [commentsForShow.length]);

  if (!commentsForShow.length) return null;

  function renderComment(
    comment: IComment,
    depth = 0,
    position = 0,
    isMain = false
  ): JSX.Element {
    const marginLeft = depth > 0 ? 10 : 0;

    if (!props.isShowed) {
      // закрытое состояние: плоская «стопка» (верхняя — последняя при map, из-за .reverse() будет сверху)
      return (
        <Card
          key={comment.id}
          id={comment.id}
          toggleActive={(state) => {
            if (position !== commentsForShow.length - 1 && state) {
              setIsHovered(true);
            } else {
              setIsHovered(false);
            }
            setActiveCommentId(state ? comment.id : "");
          }}
          color={comment.color}
          onClick={() => toggleShow(true)}
          isGlobal={props.isGlobal}
          isFocused={props.focusedCommentId === comment.id}
          onResolve={(state) => props.onResolve(comment.id, state)}
          isResolved={comment.isResolved}
          isDraft={comment.isDraft}
          isMain={isMain}
          isHovered={isHovered}
          isNew={comment.isNew}
          isShowed={false}
          position={position}
          isHidden={commentsForShow.length - position > 3 && !comment.isNew}
          cardsCount={commentsForShow.length}
          isEditable={comment.isEditable}
          from={comment.from}
          text={comment.text}
          name={comment.name}
          allManagerCommentsResolved={allManagerCommentsResolved}
        />
      );
    }

    // открытое состояние: дерево (корень + дочерние)
    return (
      <div key={comment.id} style={{ marginLeft }}>
        <Card
          id={comment.id}
          toggleActive={(state) => {
            setActiveCommentId(state ? comment.id : "");
          }}
          color={comment.color}
          onClick={() => toggleShow(true)}
          isGlobal={props.isGlobal}
          isFocused={props.focusedCommentId === comment.id}
          onResolve={(state) => props.onResolve(comment.id, state)}
          isResolved={comment.isResolved}
          isDraft={comment.isDraft}
          isMain={depth === 0}
          isHovered={false}
          isNew={comment.isNew}
          isShowed={true}
          position={0}
          isHidden={false}
          cardsCount={1}
          isEditable={comment.isEditable}
          from={comment.from}
          text={comment.text}
          name={comment.name}
          allManagerCommentsResolved={allManagerCommentsResolved}
        />
        {comment.replies?.map((reply, idx) =>
          renderComment(reply, 1, idx, false)
        )}
      </div>
    );
  }

  return (
    <div
      ref={listWrapperElement}
      className={clsxm(
        "pointer relative",
        !props.isGlobal && ["sticky", "top-6", "z-40"],
        "opacity-100 transition-opacity",
        !props.isShowed && props.isBlured ? "opacity-20" : "z-50",
        props.className
      )}
      style={{
        marginLeft: !props.useGrid && !props.isGlobal ? "48px" : undefined,
      }}
    >
      {props.isShowed && (
        <div
          style={{
            top: props.isGlobal ? "0px" : `-32px`,
            width: props.isGlobal ? "100%" : "376px",
          }}
          className={clsxm(
            "pointer absolute flex items-center justify-end gap-3 text-sm",
            props.isGlobal && "mb-1"
          )}
        >
          {ctx.isManager && resolvedCommentsCount > 0 && !allCommentsResolved && (
            <>
              <Link
                href="#"
                onClick={() => setIsResolvedShowed((prev) => !prev)}
              >
                {isResolvedShowed
                  ? t("application:comments.list.hideResolved")
                  : t("application:comments.list.showResolved")}
              </Link>
              <div className="h-[12px] w-[1px] bg-[#E6ECF4]" />
            </>
          )}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleShow(false);
            }}
          >
            {t("application:comments.list.close")}
          </Link>
        </div>
      )}

      <div className="relative">
        <div
          ref={scrollableRef}
          className="flex max-h-[calc(60vh-100px)] flex-col gap-3 overflow-y-auto pr-2 pt-5 pl-5"
        >
          {commentsForShow
            .map((comment, i) =>
              renderComment(comment, 0, i, i === commentsForShow.length - 1)
            )
            // важно: оставляем reverse — визуально получится newest → oldest
            .reverse()}
          <div className="h-[30px]" />
        </div>

        {props.isShowed && isScrollable && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-8 bg-gradient-to-b from-transparent to-white-900" />
        )}
      </div>
    </div>
  );
}

export default memo(List);
